import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Building2, Mail, Phone, MapPin, GraduationCap, Landmark } from 'lucide-react';
import { FileUpload } from '@/components/ui/file-upload';
import { EditableCardActions } from './EditableCardActions';
import { useEditableCard } from '@/hooks/useEditableCard';
import { updateSchool, getAcademies, uploadSchoolFile } from '@/api/schools';
import { AddressAutocomplete } from '@/components/ui/address-autocomplete';
import type { AddressSuggestion } from '@/api/schools';
import { getCountryOptions, countryCodeToFlag, getCountryByCode } from '@/constants/countries';
import type { SchoolEditData, SchoolOptions, UpdateSchoolData, Academie } from '@/types/school';

const { priority: PRIORITY_COUNTRIES, others: OTHER_COUNTRIES } = getCountryOptions();

interface GeneralInfoCardProps {
  school: SchoolEditData;
  options: SchoolOptions;
  onSaved: () => void;
}

interface GeneralInfoFormData {
  [key: string]: unknown;
  name: string;
  email: string | null;
  phone: string | null;
  emailSender: string | null;
  domainSender: string | null;
  networkId: number | null;
  address: string | null;
  addressComplement: string | null;
  addressHeadoffice: string | null;
  addressHeadofficeComplement: string | null;
  isEducationNationale: boolean;
  country: string;
  latitude: number | null;
  longitude: number | null;
}

export function GeneralInfoCard({ school, options, onSaved }: GeneralInfoCardProps) {
  const [academies, setAcademies] = useState<Academie[]>([]);
  const [uploadingLogo, setUploadingLogo] = useState(false);

  // Logo principal : affiché dans l'app par l'école (V1 + V2). Upload
  // indépendant du formulaire (endpoint dédié), disponible dans les deux
  // environnements depuis que l'onglet Branding est réservé à la V1.
  const handleLogoUpload = async (file: File) => {
    try {
      setUploadingLogo(true);
      await uploadSchoolFile(school.code, 'logo', file);
      onSaved();
    } catch (err) {
      console.error('Error uploading logo:', err);
    } finally {
      setUploadingLogo(false);
    }
  };

  const initialData: GeneralInfoFormData = {
    name: school.name,
    email: school.email,
    phone: school.phone,
    emailSender: school.emailSender ?? null,
    domainSender: school.domainSender,
    networkId: school.networkId,
    address: school.address,
    addressComplement: school.addressComplement ?? null,
    addressHeadoffice: school.addressHeadoffice,
    addressHeadofficeComplement: school.addressHeadofficeComplement ?? null,
    isEducationNationale: school.isEducationNationale || false,
    country: school.country || 'FR',
    latitude: school.latitude ?? null,
    longitude: school.longitude ?? null,
  };

  const { isEditing, formData, isSaving, error, startEditing, cancelEditing, updateField, save } =
    useEditableCard<GeneralInfoFormData>({
      initialData,
      onSave: async (data) => {
        const payload: UpdateSchoolData = {
          name: data.name,
          email: data.email,
          phone: data.phone,
          emailSender: data.emailSender,
          domainSender: data.domainSender,
          networkId: data.networkId,
          address: data.address,
          addressComplement: data.addressComplement,
          addressHeadoffice: data.addressHeadoffice,
          addressHeadofficeComplement: data.addressHeadofficeComplement,
          isEducationNationale: data.isEducationNationale,
          // Passage en « non Éducation nationale » : efface le RNE/académie
          // de l'établissement principal (proxifiés par le backend)
          ...(data.isEducationNationale ? {} : { rne: null, codeAcademie: null }),
          country: data.country,
          latitude: data.latitude,
          longitude: data.longitude,
        };
        await updateSchool(school.code, payload);
      },
      onSaved,
    });

  // Load academies (pour l'affichage du libellé académie en lecture)
  useEffect(() => {
    if (academies.length === 0) {
      const loadAcademies = async () => {
        try {
          const data = await getAcademies();
          setAcademies(data);
        } catch (err) {
          console.error('Error loading academies:', err);
        }
      };
      loadAcademies();
    }
  }, [academies.length]);

  if (!isEditing) {
    return (
      <Card>
        <CardHeader
          icon={<Building2 />}
          iconColor="blue"
          actions={
            <EditableCardActions
              isEditing={false}
              isSaving={false}
              onEdit={startEditing}
              onSave={save}
              onCancel={cancelEditing}
            />
          }
        >
          <CardTitle>Informations générales</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4 items-center">
            <div className="space-y-3">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Code</p>
                <p className="font-mono">{school.code}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground">Nom</p>
                <p>{school.name}</p>
              </div>
            </div>
            <div className="flex justify-center">
              {school.logoUrl ? (
                <img
                  src={school.logoUrl}
                  alt={`Logo ${school.name}`}
                  className="h-32 w-32 object-contain rounded-lg"
                />
              ) : (
                <div className="h-32 w-32 rounded-lg bg-muted flex items-center justify-center">
                  <Building2 className="h-12 w-12 text-muted-foreground/50" />
                </div>
              )}
            </div>
          </div>

          {(school.email || school.phone) && (
            <div className="grid grid-cols-2 gap-4 pt-4 border-t border-border/50">
              {school.email && (
                <div className="flex items-start gap-2">
                  <Mail className="h-4 w-4 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Email</p>
                    <p className="text-sm">{school.email}</p>
                  </div>
                </div>
              )}
              {school.phone && (
                <div className="flex items-start gap-2">
                  <Phone className="h-4 w-4 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Telephone</p>
                    <p className="text-sm">{school.phone}</p>
                  </div>
                </div>
              )}
            </div>
          )}

          {(school.address || school.country) && (
            <div className="flex items-start gap-2">
              <MapPin className="h-4 w-4 text-muted-foreground mt-0.5" />
              <div>
                <p className="text-sm font-medium text-muted-foreground">Adresse</p>
                <p className="text-sm whitespace-pre-line">{school.address}</p>
                {school.addressComplement && (
                  <p className="text-sm text-muted-foreground">{school.addressComplement}</p>
                )}
                {school.country && (
                  <p className="text-xs text-muted-foreground mt-1">
                    {countryCodeToFlag(school.country)}{' '}
                    {getCountryByCode(school.country)?.label || school.country}
                  </p>
                )}
              </div>
            </div>
          )}

          {(school.isEducationNationale || school.rne || school.codeAcademie) && (
            <div className="grid grid-cols-2 gap-4 pt-4 border-t border-border/50">
              <div className="flex items-start gap-2">
                <GraduationCap className="h-4 w-4 text-muted-foreground mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Education Nationale</p>
                  <p className="text-sm">{school.isEducationNationale ? 'Oui' : 'Non'}</p>
                </div>
              </div>
              {school.rne && (
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Code RNE</p>
                  <p className="text-sm font-mono">{school.rne}</p>
                </div>
              )}
              {school.codeAcademie && (
                <div className="flex items-start gap-2">
                  <Landmark className="h-4 w-4 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Academie</p>
                    <p className="text-sm">{academies.find((a) => a.code === school.codeAcademie)?.name || school.codeAcademie}</p>
                  </div>
                </div>
              )}
            </div>
          )}

          {school.maintenance && (
            <div className="pt-2">
              <Badge variant="destructive">Maintenance</Badge>
            </div>
          )}
        </CardContent>
      </Card>
    );
  }

  // Edit mode
  return (
    <Card>
      <CardHeader
        icon={<Building2 />}
        iconColor="blue"
        actions={
          <EditableCardActions
            isEditing={true}
            isSaving={isSaving}
            onEdit={startEditing}
            onSave={save}
            onCancel={cancelEditing}
          />
        }
      >
        <CardTitle>Informations générales</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {error && (
          <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-700">
            {error}
          </div>
        )}

        {/* Logo principal (affiché dans l'app, V1 + V2) */}
        <div className="space-y-2 max-w-xs">
          <Label>Logo principal</Label>
          <FileUpload
            previewUrl={school.logoUrl || undefined}
            onChange={() => {}}
            onUpload={handleLogoUpload}
            uploading={uploadingLogo}
            label="Logo"
            description="Affiché sur la plateforme"
          />
        </div>

        {/* Education Nationale Toggle */}
        <div>
          <div className="p-4 border rounded-lg bg-muted/30 mb-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-blue-100">
                  <GraduationCap className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <Label className="text-base font-medium">
                    Etablissement Education Nationale
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    École reconnue par l'Éducation Nationale française
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-1 bg-muted rounded-lg p-1">
                <button
                  type="button"
                  className={`w-12 py-1 text-sm font-medium rounded-md transition-colors text-center ${
                    formData.isEducationNationale
                      ? 'bg-white shadow-sm text-foreground'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                  onClick={() => updateField('isEducationNationale', true)}
                >
                  Oui
                </button>
                <button
                  type="button"
                  className={`w-12 py-1 text-sm font-medium rounded-md transition-colors text-center ${
                    !formData.isEducationNationale
                      ? 'bg-white shadow-sm text-foreground'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                  onClick={() => updateField('isEducationNationale', false)}
                >
                  Non
                </button>
              </div>
            </div>
          </div>

          <p className="text-xs text-muted-foreground">
            Le RNE, l'académie et l'adresse de chaque établissement se gèrent dans l'onglet
            « Établissements ».
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="gen-name">Nom de l'école</Label>
            <Input
              id="gen-name"
              value={formData.name || ''}
              onChange={(e) => updateField('name', e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="gen-email">Email de contact</Label>
            <Input
              id="gen-email"
              type="email"
              value={formData.email || ''}
              onChange={(e) => updateField('email', e.target.value || null)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="gen-phone">Telephone</Label>
            <Input
              id="gen-phone"
              value={formData.phone || ''}
              onChange={(e) => updateField('phone', e.target.value || null)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="gen-emailSender">Email expediteur</Label>
            <Input
              id="gen-emailSender"
              type="email"
              value={formData.emailSender || ''}
              onChange={(e) => updateField('emailSender', e.target.value || null)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="gen-domainSender">Domaine expediteur</Label>
            <Input
              id="gen-domainSender"
              value={formData.domainSender || ''}
              onChange={(e) => updateField('domainSender', e.target.value || null)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="gen-network">Réseau virtuel</Label>
            <Select
              value={formData.networkId?.toString() || 'none'}
              onValueChange={(value) =>
                updateField('networkId', value === 'none' ? null : parseInt(value))
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="Selectionner" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Aucun</SelectItem>
                {options.networks
                  .filter((n) => n.type !== 'physical')
                  .map((n) => (
                    <SelectItem key={n.id} value={n.id.toString()}>
                      {n.name}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="gen-country">Pays</Label>
            <Select
              value={formData.country || 'FR'}
              onValueChange={(value) => {
                updateField('country', value);
                updateField('latitude', null);
                updateField('longitude', null);
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Sélectionner un pays" />
              </SelectTrigger>
              <SelectContent>
                {PRIORITY_COUNTRIES.map((c) => (
                  <SelectItem key={c.code} value={c.code}>
                    {countryCodeToFlag(c.code)} {c.label}
                  </SelectItem>
                ))}
                <div className="border-t my-1" />
                {OTHER_COUNTRIES.map((c) => (
                  <SelectItem key={c.code} value={c.code}>
                    {countryCodeToFlag(c.code)} {c.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="gen-address">Adresse</Label>
            <AddressAutocomplete
              id="gen-address"
              value={formData.address || ''}
              country={formData.country || 'FR'}
              onChange={(address) => updateField('address', address || null)}
              onSelect={(suggestion: AddressSuggestion) => {
                updateField('address', suggestion.label);
                updateField('latitude', suggestion.latitude);
                updateField('longitude', suggestion.longitude);
              }}
              placeholder="Saisir une adresse..."
            />
            <Input
              id="gen-addressComplement"
              value={formData.addressComplement || ''}
              onChange={(e) => updateField('addressComplement', e.target.value || null)}
              placeholder="Complément d'adresse"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="gen-addressHq">Siège social</Label>
            <AddressAutocomplete
              id="gen-addressHq"
              value={formData.addressHeadoffice || ''}
              country={formData.country || 'FR'}
              onChange={(address) => updateField('addressHeadoffice', address || null)}
              onSelect={(suggestion: AddressSuggestion) => {
                updateField('addressHeadoffice', suggestion.label);
              }}
              placeholder="Adresse du siège social"
            />
            <Input
              id="gen-addressHqComplement"
              value={formData.addressHeadofficeComplement || ''}
              onChange={(e) => updateField('addressHeadofficeComplement', e.target.value || null)}
              placeholder="Complément d'adresse"
            />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
